import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";

export interface LobCurrency {
  code: string;
  symbol: string;
  locale: string;
}

export interface LobCountryCode {
  mode: "fixed" | "dynamic";
  fixedDialCode?: string;
}

export interface LobRegion {
  country: string;
  countryName: string;
  // false hides India/eRupi-only inputs (Voucher Type, Merchant ID, IFSC).
  indiaFields: boolean;
  // Label of the tax shown on billing screens (GST for India, SST for Malaysia).
  taxLabel: string;
}

export interface LobTimezone {
  code: string;
  iana: string;
  label: string;
}

export const DEFAULT_LOB_REGION: LobRegion = { country: "IND", countryName: "India", indiaFields: true, taxLabel: "GST" };

export const DEFAULT_LOB_TIMEZONE: LobTimezone = { code: "IST", iana: "Asia/Kolkata", label: "IST (India)" };

// What every screen showed before currency became per-LoB. Only used until
// the real config arrives (the app shell holds rendering until then) and as
// the fallback if the request fails.
export const DEFAULT_LOB_CURRENCY: LobCurrency = { code: "INR", symbol: "₹", locale: "en-IN" };

interface LobConfigState {
  lobName: string | null;
  lobCode: string | null;
  timezone: LobTimezone;
  currency: LobCurrency;
  countryCode: LobCountryCode;
  region: LobRegion;
  loaded: boolean;
}

const initialState: LobConfigState = {
  lobName: null,
  lobCode: null,
  timezone: DEFAULT_LOB_TIMEZONE,
  currency: DEFAULT_LOB_CURRENCY,
  countryCode: { mode: "fixed", fixedDialCode: "+91" },
  region: DEFAULT_LOB_REGION,
  loaded: false,
};

export const fetchLobConfig = createAsyncThunk("lobConfig/fetch", async (_, { rejectWithValue }) => {
  try {
    const response = await axios.request({
      baseURL: `${window.env.API_URL}/v1/programs/lob-config`,
      timeout: 10000,
      headers: {
        Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        "Content-Type": "application/json",
      },
      method: "GET",
    });
    return response.data as {
      lobName?: string;
      lobCode?: string;
      currency: LobCurrency;
      timezone?: LobTimezone;
      country_code: LobCountryCode;
      region: LobRegion;
    };
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || "Failed to load LoB config");
  }
});

const lobConfigSlice = createSlice({
  name: "lobConfig",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchLobConfig.pending, (state) => {
        // A different user may have just logged in - never show the previous
        // user's currency while the new one is loading.
        state.loaded = false;
      })
      .addCase(fetchLobConfig.fulfilled, (state, action) => {
        state.currency = action.payload.currency || DEFAULT_LOB_CURRENCY;
        state.countryCode = action.payload.country_code || state.countryCode;
        state.region = action.payload.region
          ? { ...action.payload.region, taxLabel: action.payload.region.taxLabel || DEFAULT_LOB_REGION.taxLabel }
          : DEFAULT_LOB_REGION;
        state.timezone = action.payload.timezone || DEFAULT_LOB_TIMEZONE;
        state.lobName = action.payload.lobName || null;
        state.lobCode = action.payload.lobCode || null;
        state.loaded = true;
      })
      .addCase(fetchLobConfig.rejected, (state) => {
        state.currency = DEFAULT_LOB_CURRENCY;
        state.region = DEFAULT_LOB_REGION;
        state.timezone = DEFAULT_LOB_TIMEZONE;
        state.lobName = null;
        state.lobCode = null;
        state.loaded = true;
      });
  },
});

export default lobConfigSlice.reducer;
